import React, { useEffect, useState } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { fetchSeasonBreakdown } from '../../utils/pointsApi';
import { getCurrentSeasonId, getDaysRemainingInSeason } from '../../constants/pointValues';
import './PointsBreakdownModal.css';

// Human-readable labels for each point reason
const REASON_LABELS = {
  game_win: 'Game Wins',
  game_loss: 'Game Losses',
  daily_checkin: 'Daily Check-ins',
  first_game_of_day: 'First Game of Day',
  powerup_use: 'Powerup Usage',
  streak_3: '3-Day Streak Bonus',
  streak_7: '7-Day Streak Bonus',
  streak_14: '14-Day Streak Bonus',
};

const REASON_ORDER = [
  'game_win',
  'game_loss',
  'daily_checkin',
  'first_game_of_day',
  'powerup_use',
  'streak_3',
  'streak_7',
  'streak_14',
];

const PointsBreakdownModal = ({ onClose }) => {
  const { seasonPoints, lifetimePoints, checkinStreak } = useProfile();
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  const seasonId = getCurrentSeasonId();
  const daysRemaining = getDaysRemainingInSeason();

  useEffect(() => {
    let cancelled = false;

    fetchSeasonBreakdown().then((result) => {
      if (cancelled) return;
      setBreakdown(result);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  return (
    <div className="points-modal-overlay" onClick={onClose}>
      <div className="points-modal" onClick={(e) => e.stopPropagation()}>
        <button className="points-modal-close" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <h2 className="points-modal-title">My Points</h2>
        <p className="points-modal-subtitle">
          Season {seasonId + 1} &middot; {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
        </p>

        {/* Summary cards */}
        <div className="points-modal-summary">
          <div className="points-modal-card">
            <span className="points-modal-card-value">{(seasonPoints ?? 0).toLocaleString()}</span>
            <span className="points-modal-card-label">Season Points</span>
          </div>
          <div className="points-modal-card">
            <span className="points-modal-card-value">{(lifetimePoints ?? 0).toLocaleString()}</span>
            <span className="points-modal-card-label">Lifetime Points</span>
          </div>
          <div className="points-modal-card">
            <span className="points-modal-card-value">{checkinStreak ?? 0}</span>
            <span className="points-modal-card-label">Check-in Streak</span>
          </div>
        </div>

        {/* Breakdown table */}
        <div className="points-modal-breakdown">
          <h3>Season Breakdown</h3>
          {loading ? (
            <p className="points-modal-loading">Loading...</p>
          ) : !breakdown ? (
            <p className="points-modal-empty">No points data yet.</p>
          ) : (
            <table className="points-modal-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Points</th>
                </tr>
              </thead>
              <tbody>
                {REASON_ORDER
                  .filter((reason) => breakdown.breakdown[reason] > 0)
                  .map((reason) => (
                    <tr key={reason}>
                      <td>{REASON_LABELS[reason] || reason}</td>
                      <td className="points-modal-pts">{breakdown.breakdown[reason].toLocaleString()}</td>
                    </tr>
                  ))
                }
                {Object.keys(breakdown.breakdown).length === 0 && (
                  <tr>
                    <td colSpan={2} style={{ textAlign: 'center', opacity: 0.6 }}>
                      Play games to earn points!
                    </td>
                  </tr>
                )}
              </tbody>
              {breakdown.total > 0 && (
                <tfoot>
                  <tr>
                    <td><strong>Total</strong></td>
                    <td className="points-modal-pts"><strong>{breakdown.total.toLocaleString()}</strong></td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PointsBreakdownModal;
